// SPEC-PWA-SW-STRIP-PHASE1-01: savepoint。カスタムSW更新層全廃。phase2でautoUpdate化予定。
// SPEC-SW-UPDATE-TRIGGER-01: iOSはPWAプロセスを任意タイミングでkillするため、SW更新の"発見"を
// フルページロードのみに依存させない。visibilitychange復帰時 + 15分間隔でregistration.update()を
// 呼び、直近60秒以内のチェックは両トリガー共有でスキップ。offline等のupdate()失敗はsilent
// (console.warnのみ、UIバナーなし)。
// SPEC-PWA-SW-UPDATE-FIX-A-01 (D-109): registerType='prompt' を正しく完成。skipWaiting/clientsClaim(層3)を撤去し
// (vite.config)、onNeedRefresh で「更新ありフラグ→控えめバナー→タップで updateSW(true)」の prompt 本道を実装。
// SPEC-PWA-SW-IOS-DETECT-FIX-01 (D-111): iOS Safari は updatefound/onNeedRefresh を取りこぼす(起動前に SW を更新して
// installing に気づく隙を与えない)。onNeedRefresh イベント依存をやめ registration.waiting 実在チェックに切替
// (イベントは非iOS用に保険併用)。(a)onRegisteredSW 起動時 waiting チェック + update() 即実行→解決後 waiting 再チェック、
// (b)createUpdateChecker の update() 後 waiting ポーリング。自動 reload は依然しない(適用はバナー明示タップのみ)。
import { registerSW } from 'virtual:pwa-register'
import { BUILD_SHA } from './buildInfo'
// D-1041 (2): install の開始・成功・失敗を既存の auth_trace に貯める(ログイン成功時にまとめて送られる)。
import { traceAuth, TRACE } from './authTrace'

export const UPDATE_INTERVAL_MS = 15 * 60 * 1000
export const MIN_CHECK_GAP_MS = 60 * 1000

// SPEC-VERSION-VISIBILITY-AND-UPDATE-V1 (D-456) ITEM_5: 手動「更新を確認」は即答しない。
//   update() 解決直後の waiting は iOS で空振りするため、数秒 waiting をポーリングしてから判定する(D-111/112 の罠)。
export const MANUAL_CHECK_POLL_MS = 5000
export const MANUAL_CHECK_POLL_STEP_MS = 500

// ITEM_5: 手動チェックは登録済み registration を必要とする。onRegisteredSW で受けた参照を保持する。
let _registration = null
// D-1041: 直近に version.json で見た新しい版(install 失敗の記録用)。
let _lastRemoteVersion = null
export function getStoredRegistration() { return _registration }
export function __setRegistrationForTest(r) { _registration = r }

// ITEM_2/ITEM_3: version.json を no-store + 毎回変わるクエリで取得(古い判定ファイルを見続けない)。
//   戻り値は {version, build_number, sha, build_time, minimum_supported}。取得/parse 失敗は throw。
export async function fetchRemoteVersion({
  fetchImpl = (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined),
  now = () => Date.now(),
} = {}) {
  if (!fetchImpl) throw new Error('no fetch')
  const res = await fetchImpl(`/version.json?t=${now()}`, { cache: 'no-store' })
  if (!res || !res.ok) throw new Error(`version.json ${res && res.status}`)
  const data = await res.json()
  if (!data || !data.sha) throw new Error('version.json malformed')
  // D-1041 (2): install 失敗の記録に「どの版を入れようとして失敗したか」を付けるため控える。
  if (data.version) _lastRemoteVersion = String(data.version)
  return data
}

// ITEM_5: 手動チェック本体。60秒ガード(MIN_CHECK_GAP_MS)をバイパスし、version.json と SW waiting の
//   両方を見る。どちらかが新版を示せば update-available。取得失敗は error(=「確認できませんでした」)で、
//   最新ですとは絶対に返さない。戻り値: { status:'update-available'|'latest'|'error', remote? }。
export async function manualCheckForUpdate({
  registration = _registration,
  currentSha = BUILD_SHA,
  fetchImpl,
  sleep = (ms) => new Promise(r => setTimeout(r, ms)),
  now = () => Date.now(),
  pollMs = MANUAL_CHECK_POLL_MS,
  stepMs = MANUAL_CHECK_POLL_STEP_MS,
} = {}) {
  // (1) version.json を取得(失敗しても SW waiting は見る=判定材料を1つに依存させない)。
  let remote = null
  let fetchOk = false
  try {
    remote = await fetchRemoteVersion({ fetchImpl, now })
    fetchOk = true
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[ERR-SW-MANUAL-001] version.json fetch failed:', e?.message)
  }
  // version.json が現行と別 sha なら、その時点で新版あり(ポーリング不要)。
  if (fetchOk && remote?.sha && remote.sha !== currentSha) {
    if (registration?.waiting) markNeedRefresh()
    return { status: 'update-available', remote }
  }
  // (2) SW を明示 update()(60秒ガードを通さない)。オフライン等の失敗は無視。
  try { await registration?.update?.() } catch { /* offline 正常系 */ }
  // (3) 数秒 waiting をポーリング(iOS の遅延検知)。UI 側はこの間「確認中」を出す。
  const deadline = now() + pollMs
  // 先に一度見てから待つ(既に waiting 済のケースを即拾う)。
  for (;;) {
    if (registration?.waiting) { markNeedRefresh(); return { status: 'update-available', remote } }
    if (now() >= deadline) break
    await sleep(stepMs)
  }
  // (4) 判定。取得失敗は「確認できませんでした」。最新です は取得成功かつ waiting 無しのときだけ。
  if (!fetchOk) return { status: 'error' }
  return { status: 'latest', remote }
}

// テスト用 export: guardロジックを純粋に検証可能にするためファクトリ化
export function createUpdateChecker(registration, now = () => Date.now()) {
  let lastCheckAt = -Infinity // "未チェック"の番兵。0だと小さいタイムスタンプ基準の環境で誤判定しうる
  return function checkForUpdate() {
    const t = now()
    if (t - lastCheckAt < MIN_CHECK_GAP_MS) return false
    lastCheckAt = t
    registration.update()
      .then(() => {
        // D-111: iOS は updatefound/onNeedRefresh を取りこぼすため、update() 解決後に waiting 実在をポーリングで拾う。
        if (registration.waiting) markNeedRefresh()
      })
      .catch(() => {
        // オフライン等でのupdate()失敗は正常系。UIバナーなし、console.errorも出さない。
        console.warn('[ERR-SW-UPDATE-001] SW update check failed (offline?)')
        // D-1041 直し②: 画面には出さず記録だけ残す(版ごとに 1 回まで)。
        _traceUpdateReject()
      })
    return true
  }
}

// 戻り値のcleanupはproduction側では未使用(アプリ生存期間中ずっと有効)、テストでの
// リスナー/タイマー後片付け用。
export function setupPeriodicUpdateCheck(registration) {
  if (!registration) return () => {}
  const checkForUpdate = createUpdateChecker(registration)
  const intervalId = setInterval(checkForUpdate, UPDATE_INTERVAL_MS)
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') checkForUpdate()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  return () => {
    clearInterval(intervalId)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}

// SPEC-PWA-SW-UPDATE-FIX-A-01 (D-109): 「更新ありフラグ」の購読可能ストア。
// 新SW waiting検知 (onNeedRefresh) でフラグを立て、控えめトースト常駐バナー (SwUpdateBanner) に伝える。
// 自動 reload は絶対にしない。適用はスタッフが安全なタイミングで明示タップした時のみ (applyUpdate=updateSW(true))。
let _needRefresh = false
const _listeners = new Set()
function _emitNeedRefresh() {
  for (const cb of _listeners) cb(_needRefresh)
}

// 現在値で即コールバック + 変化時に通知。unsubscribe を返す。
export function subscribeNeedRefresh(cb) {
  _listeners.add(cb)
  cb(_needRefresh)
  return () => _listeners.delete(cb)
}

export function getNeedRefresh() {
  return _needRefresh
}

function markNeedRefresh() {
  // SPEC-PWA-SW-UPDATE-REDUCTION-01 (D-112) AC6: 二重発火ガード。複数検知経路(起動時waiting/statechange/
  // update()後ポーリング/onNeedRefreshイベント)が同一 waiting SW に対して叩いても、バナーは一度だけ。
  if (_needRefresh) return
  _needRefresh = true
  _emitNeedRefresh()
}

// テスト専用: production では未使用。_needRefresh を初期化して各ケースを独立させる。
export function __resetNeedRefreshForTest() {
  _needRefresh = false
}

// SPEC-SW-UPDATE-COMPLETE-AND-RELOCATE-01 (D-148) ITEM_1: 更新適用の3段目(controllerchange→reload)を補完。
//   背景: reload を撃つ controlling リスナは vite-plugin-pwa の showSkipWaitingPrompt 内でのみ登録されるが、
//   本アプリは iOS 対策(D-111/112)で独自 waiting 検知に切替た際その経路を通らなくなり、タップ→skipWaiting までは
//   進むが reload が起きず「適用中…」で永久停止していた(D-147 で確定)。ここで controllerchange を明示登録して補う。
//   ガード(重要): controllerchange はタップ無しでも起き得る(他タブでの適用等)。「この端末でユーザーがタップした」
//   ことを示す旗 _userTriggeredApply でガードし、タップ由来でない controllerchange では reload しない(入力中の画面飛び防止)。
let _userTriggeredApply = false
let _reloadedOnControllerChange = false // 二重発火防止

export function __resetApplyFlagsForTest() {
  _userTriggeredApply = false
  _reloadedOnControllerChange = false
}

export function setupControllerChangeReload(reload = () => window.location.reload()) {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return () => {}
  const onChange = () => {
    if (!_userTriggeredApply) return          // タップ由来でない controllerchange は無視(自動 reload を復活させない)
    if (_reloadedOnControllerChange) return   // 二重発火ガード(reload は一度だけ)
    _reloadedOnControllerChange = true
    reload()
  }
  navigator.serviceWorker.addEventListener('controllerchange', onChange)
  return () => navigator.serviceWorker.removeEventListener('controllerchange', onChange)
}

// ───────────────────────────────────────────────────────────
// D-1041 (1): 新しい SW の install の状態を画面に配る購読ストア。
//   iPhone のホーム画面アプリで新しい SW の install が終わらない/失敗すると waiting が出来ず、
//   利用者には「準備中」のまま・司令塔にも理由が見えなかった。
//   phase: 'idle' | 'installing' | 'installed' | 'failed'
//     failed = installing の worker が installed に届かずに redundant になった(=install 失敗)。
//     installed の後に redundant になるのは別の新版に置き換わった正常系なので failed にしない。
//   ★ここは見せるだけ。自動 reload / skipWaiting / clientsClaim の経路は足さない(D-109/D-148)。
let _install = { phase: 'idle', startedAt: null }
const _installListeners = new Set()
function _setInstall(next) {
  _install = next
  for (const cb of _installListeners) cb(_install)
}
export function subscribeInstallState(cb) {
  _installListeners.add(cb)
  cb(_install)
  return () => _installListeners.delete(cb)
}
export function getInstallState() { return _install }
export function __resetInstallStateForTest() {
  _install = { phase: 'idle', startedAt: null }
  _installListeners.clear()
  _lastRemoteVersion = null
  _unknownTraced.clear() // 版が分からない記録の「この読み込みで記録済み」はメモリだけ(読み直しで消える)
  // ★端末側の「記録済み」(SW_TRACE_ONCE_KEY)は消さない。ページの読み直しをまたいで効く必要があるため。
}

// D-1041 (2): auth_trace の reason。サーバ(fn_log_auth_trace)は [A-Za-z0-9_-] 以外を消して 32 文字で切るので、
//   その範囲に収まる形で作る。出来事名はサーバの決め打ち 8 個から page_load を借りる(migration は FORBIDDEN)。
//   app_version 列には今動いている(古い)版が入り、失敗時は入れようとした版を reason の末尾 _v に付ける。
export function installTraceReason(kind, sec, remoteVersion) {
  const s = Math.min(9999, Math.max(0, Math.round(Number(sec) || 0)))
  if (kind === 'ok') return `sw_install_ok_${s}s`
  if (kind === 'fail') {
    const v = remoteVersion ? `_v${String(remoteVersion).replace(/[^0-9A-Za-z]/g, '-')}` : ''
    return `sw_install_fail_${s}s${v}`.slice(0, 32)
  }
  if (kind === 'retry') return 'sw_install_retry'
  return 'sw_install_start'
}
// D-1041 直し③: 端末の足跡(authTrace)は 50 件が上限。install の失敗が続くと D-838 のログイン足跡
//   (pin_submit / guard_redirect 等)が押し出される。sw_* は「入れようとした版」ごとに
//   start / ok / fail / reject / retry を各 1 回までにする。ページを読み直すたびに失敗する端末で効くよう、
//   記録済みの印は localStorage に置く(メモリだと読み直しで消える)。
//   覚えておく版は直近 SW_TRACE_ONCE_MAX_VERSIONS 個まで(印そのものが膨らまないように)。
// D-1041 直し2回目(1): 版が分からない(version.json をまだ見ていない)ときは端末の印を使わない。
//   'unknown' の印を端末に置くと、2 版目以降の start や起動時の reject がずっと記録されなくなる(gate_3 再判定 FAIL)。
//   代わりに 1 回のページ読み込みの間だけ、種類ごとに 1 回までにする(メモリの _unknownTraced)。
//   page_load はもともと読み込みごとに 1 件なので、読み込みごとに数件増えても D-838 の足跡は押し出されない。
//   1 回目の直しが端末に残した 'unknown' の印は読んでも無視し、次に印を書くときに捨てる。
export const SW_TRACE_ONCE_KEY = 'd1041_sw_trace_once'
const SW_TRACE_ONCE_MAX_VERSIONS = 5
const SW_TRACE_UNKNOWN_KEY = 'unknown'
const _unknownTraced = new Set()
function _traceOnce(kind, version, emit) {
  try {
    if (!version) {
      if (_unknownTraced.has(kind)) return false
      _unknownTraced.add(kind)
      emit()
      return true
    }
    const v = String(version)
    let seen = {}
    try { seen = JSON.parse(localStorage.getItem(SW_TRACE_ONCE_KEY) || '{}') || {} } catch { seen = {} }
    const kinds = Array.isArray(seen[v]) ? seen[v] : []
    if (kinds.includes(kind)) return false
    // 新しい版は末尾へ(並び順 = 古い順)。上限を超えたら古い版から忘れる。古い 'unknown' の印はここで捨てる。
    const next = {}
    const keep = Object.keys(seen).filter(k => k !== v && k !== SW_TRACE_UNKNOWN_KEY)
    for (const k of keep.slice(-(SW_TRACE_ONCE_MAX_VERSIONS - 1))) next[k] = seen[k]
    next[v] = [...kinds, kind]
    try { localStorage.setItem(SW_TRACE_ONCE_KEY, JSON.stringify(next)) } catch { /* 印が置けなくても記録は続ける */ }
    emit()
    return true
  } catch { return false }
}
function _traceInstall(kind, sec, remoteVersion) {
  _traceOnce(kind, remoteVersion, () => {
    try { traceAuth(TRACE.pageLoad, installTraceReason(kind, sec, remoteVersion), {}) } catch { /* 計測は止めない */ }
  })
}
// D-1041 直し②: 自動の update()(起動時・15 分ごと・前面復帰)が reject された = sw.js 自体が取れなかった。
//   通信の一時的な失敗(圏外・電波の弱い店内)と区別できないので画面には出さない(SPEC-SW-UPDATE-TRIGGER-01 の
//   「offline の失敗は silent」を守る)。記録だけ残し、圏外だったかは detail.offline(サーバが通す 6 キーの 1 つ)で分ける。
export const UPDATE_REJECT_REASON = 'sw_update_reject'
function _traceUpdateReject() {
  _traceOnce('reject', _lastRemoteVersion, () => {
    try {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      traceAuth(TRACE.pageLoad, UPDATE_REJECT_REASON, { offline })
    } catch { /* 計測は止めない */ }
  })
}

// 1 つの worker の install を見張る(同じ worker を二度見ても記録は 1 回)。更新(controller あり)のときだけ。
const _installTracked = new WeakSet()
function _observeInstall(sw, remoteVersion, registration) {
  if (!sw || typeof sw !== 'object') return
  if (!navigator.serviceWorker?.controller) return
  if (_installTracked.has(sw)) return
  _installTracked.add(sw)
  let startedAt = null
  let waitingAtStart = null // 直し2回目(2): この worker の install が始まった時点で既に居た waiting
  let done = false
  const onState = () => {
    if (done) return
    const st = sw.state
    if (st === 'parsed' || st === 'installing') {
      if (startedAt == null) {
        startedAt = Date.now()
        waitingAtStart = registration?.waiting || null
        _setInstall({ phase: 'installing', startedAt })
        _traceInstall('start', 0, remoteVersion())
      }
      return
    }
    if (st === 'installed' || st === 'activating' || st === 'activated') {
      done = true
      if (startedAt == null) return // 見始めた時点で既に install 済み(記録する所要時間が無い)
      _setInstall({ phase: 'installed', startedAt })
      _traceInstall('ok', (Date.now() - startedAt) / 1000, remoteVersion())
      return
    }
    if (st === 'redundant') {
      done = true
      if (startedAt == null) return
      const failedAt = Date.now()
      // D-1041 直し①: install の途中で次の版に置き換えられて redundant になるのは正常(失敗ではない)。
      //   redundant の通知と registration.installing の差し替えの順序はブラウザ任せなので、
      //   その場では決めず、1 タスク後に registration を見て決める。
      setTimeout(() => {
        const inst = registration?.installing
        const wait = registration?.waiting
        // D-1041 直し2回目(2): waiting が「この worker の install 開始時から居た古い worker」のままなら、
        //   置き換えではなくこの worker の失敗。fail は記録する。ただし適用できる古い版が手元にあるので
        //   画面は失敗にせず installed(更新の案内)のままにする。
        if (!(inst && inst !== sw) && wait && wait !== sw && wait === waitingAtStart) {
          if (_install.phase === 'installing' && _install.startedAt === startedAt) {
            _setInstall({ phase: 'installed', startedAt })
          }
          _traceInstall('fail', (failedAt - startedAt) / 1000, remoteVersion())
          return
        }
        if ((inst && inst !== sw) || (wait && wait !== sw)) {
          // 置き換え先の worker は自分の _observeInstall で installing / installed を出す。
          // 置き換え先が見始める前に install 済み(waiting)で、画面がこの worker の installing のままなら installed にする。
          if (!inst && _install.phase === 'installing' && _install.startedAt === startedAt) {
            _setInstall({ phase: 'installed', startedAt })
          }
          return
        }
        _setInstall({ phase: 'failed', startedAt })
        _traceInstall('fail', (failedAt - startedAt) / 1000, remoteVersion())
      }, 0)
    }
  }
  onState()
  sw.addEventListener?.('statechange', onState)
}

// D-1041 (1):「もう一度準備する」。registration.update() を呼ぶだけ(reload / 適用はしない)。
//   失敗の表示を下ろして次の installing を待つ。update() 自体が失敗したら(SW スクリプトが取れない等)failed に戻す。
export async function retryInstall(registration = _registration) {
  _traceInstall('retry', 0, _lastRemoteVersion)
  _setInstall({ phase: 'idle', startedAt: null })
  try {
    await registration?.update?.()
  } catch {
    _setInstall({ phase: 'failed', startedAt: null })
    return false
  }
  if (registration?.waiting) markNeedRefresh()
  return true
}

// SPEC-PWA-SW-UPDATE-REDUCTION-01 (D-112): SW更新検知の本命=installing worker の statechange 購読。
// iOS Safari は起動後に新SWを installing に載せ、install 完了(installed)が update() 解決の後になる回がある
// (D-111 の起動時/解決時 waiting チェックが空振りする真因)。installing→installed 遷移の瞬間に waiting を見て発火。
// controller 存在を条件に「更新(既存SWあり)」に限定し、初回インストール(controller無し)では発火しない。
// D-1041: 同じ track() で install の状態(_observeInstall)も見る。updatefound の購読は増やさない。
//   statechange は _observeInstall が 1 worker につき 1 本足す(WeakSet で同じ worker には 1 本だけ)。
export function watchInstalling(registration, { remoteVersion = () => _lastRemoteVersion } = {}) {
  if (!registration) return
  const onInstalled = (sw) => {
    if (sw?.state !== 'installed') return
    // 更新であって初回インストールでない (=既に active controller が居る) 時のみ。
    if (!navigator.serviceWorker?.controller) return
    if (registration.waiting) markNeedRefresh()
  }
  const track = (sw) => {
    if (!sw) return
    _observeInstall(sw, remoteVersion, registration)
    onInstalled(sw) // 既に installed 済のケースも即拾う
    sw.addEventListener?.('statechange', () => onInstalled(sw))
  }
  // (1) 取得時点で既に installing 中の worker
  track(registration.installing)
  // (2) 以後に現れる新規 installing worker
  registration.addEventListener?.('updatefound', () => track(registration.installing))
}

export const updateSW = registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return
    // D-456 ITEM_5: 手動「更新を確認」から registration.update()/waiting を叩けるよう参照を保持。
    _registration = registration
    // D-111 (a): 起動時に waiting 実在を能動チェック (起動前に更新済で既に waiting の回を拾う正経路)。
    if (registration.waiting) markNeedRefresh()
    // D-112 検知の本命: installing→installed 遷移の statechange 購読。update()解決後にwaiting化する回を確実に拾う。
    watchInstalling(registration)
    // D-148 ITEM_1: アプリ起動時に1回だけ controllerchange リスナを登録(reload はタップ由来のみ、二重発火ガード付き)。
    //   自前 waiting 検知(D-111/112)と同じ層に揃える。自動 reload は復活させない(タップ無しでは reload しない)。
    setupControllerChangeReload()
    // D-111 (b): KILL 再起動時の初回チェック確保 — 起動時に1回 update() を即実行 (statechange のトリガ)。
    //   解決後 waiting ポーリングも残すが、検知は markNeedRefresh の冪等ガードで二重発火しない (D-112 AC6)。
    Promise.resolve(registration.update?.())
      .then(() => { if (registration.waiting) markNeedRefresh() })
      .catch(() => { _traceUpdateReject() }) // D-1041 直し②: 画面には出さず記録だけ(版ごとに 1 回まで)
    setupPeriodicUpdateCheck(registration)
  },
  // SPEC-PWA-SW-UPDATE-FIX-A-01 (D-109): 筋A。新SW waiting検知で「更新ありフラグ」を立てバナー表示するのみ。
  // 自動 reload / updateSW(true) の自動呼び出しは絶対にしない (autoUpdate相当の暴発=穴1 再現になる)。
  // 適用はユーザーがバナーを明示タップした時だけ (applyUpdate)。updateSW(true) が controllerchange待ち→reload を内蔵。
  onNeedRefresh() {
    markNeedRefresh()
  },
})

// バナータップ時に1回だけ呼ぶ更新適用。updateSW(true) = skipWaiting相当 + controllerchange待ち + reload を内蔵。
// 自前の多段 reload 待ちは不要 (層1 versionReload は保険として残置、競合相手の層3 skipWaiting が消えたので単独で綺麗に効く)。
export function applyUpdate() {
  // D-148 ITEM_1: このタップ由来の controllerchange のみ reload 対象にする旗を立てる(タップ無し reload を封じる)。
  _userTriggeredApply = true
  return updateSW(true)
}
